import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Box, Card, MenuItem, Skeleton, TextField, Typography} from "@mui/material";
import UpsertProductCategoryDialog from "../../../../../components/dialogs/upsert-product-category";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import useIndustry from "../../../../../hooks/use-industry";
import CategoriesTreeView from "./tree";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Api from "../../../../../../core/services/api/api";
import {useBizFileUrlsResolver} from "../../../../../hooks/use-biz-file-urls-resolver";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const initialUpsertCategoryDialogState = {
    open: false,
    industryId: null,
    parentId: null,
    category: null,
    isRoot: false,
    onSuccess: () => void 0,
}

const CategoryManagementView = () => {
    const [removingIds, setRemovingIds] = useState([]);

    const [loadingCategories, setLoadingCategories] = useState(false);
    const [upsertCategoryDialogState, setUpsertCategoryDialogState] = useState(initialUpsertCategoryDialogState);
    const [categories, setCategories] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);

    const {
        industries,
        cachedIndustry,
        fetchIndustry,
        fetchedIndustry,
        loadingIndustries,
        invalidateCategoriesCache,
        hasNoIndustries,
    } = useIndustry({noCategories: true});

    const {configureNotebookTags} = useConfigureNotebook('category-management-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsCategoryManagement);
        configureBehavioralTool((tags) => tags.productsCategoryManagement);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetch categories for the selected industry
     * @type {(function(*): Promise<void>)}
     */
    const getCategories = useCallback(async (industryId) => {
        setLoadingCategories(true);
        const response = await Api.getAllPartCategoriesNew(industryId);
        if (!isMounted())
            return;
        setLoadingCategories(false);
        if (response?.resultFlag) {
            response.data = response.data?.map(e => ({
                id: e.id,
                title: e.title,
                parentId: e.parentId,
                active: e.selected,
                editable: !e.defaultAndNotEditable,
                isPredefined: e.defaultAndNotEditable,
                color: e.backgroundColor,
                coverImage: e.coverFileName,
            })) ?? []
            for (const e of response.data) {
                e.coverImage = bizFileUrls.getFilePreviewUrl(e.coverImage);
            }
            setCategories(response.data);
        }
    }, [isMounted, bizFileUrls])

    /**
     * With each change in fetchedIndustry:
     * - Fetch categories for the selected industry
     */
    useEffect(() => {
        if (!fetchedIndustry?.id)
            return;
        void getCategories(fetchedIndustry?.id);
    }, [fetchedIndustry?.id, getCategories]);

    /**
     * Remove category from the list of categories
     * @type {(function(*, *): Promise<boolean>)}
     */
    const removeCategory = useCallback(async (category, replacement) => {
        setRemovingIds(prevState => [...prevState, category.id]);
        const response = await Api.removePartCategory({
            categoryId: category.id,
            industryId: fetchedIndustry.id,
            newCategoryId: replacement?.id,
        })
        if (!isMounted())
            return false;
        setRemovingIds(prevState => prevState.filter(e => e !== category.id));
        if (response?.resultFlag) {
            if (replacement) {
                setCategories(prevState => prevState
                    .filter(e => e.id !== category.id)
                    .map(e => e.parentId === category.id ? {...e, parentId: replacement.id} : e)
                );
                invalidateCategoriesCache(fetchedIndustry?.id);
            }
        }
        return response?.resultFlag;
    }, [isMounted, invalidateCategoriesCache, fetchedIndustry?.id])

    /**
     * Gets the user confirmation to remove the category.
     * @type {(function(*): Promise<void>)}
     */
    const getRemoveConfirmation = useCallback(async (category, categoriesToReplaceWith) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            title: "Are you sure you want to remove this category? Note that you need to select a replacement category so the products" +
                " can be successfully moved",
            data: !categoriesToReplaceWith?.length ? null : categoriesToReplaceWith.filter(e => e.id !== category.id)
                .map(e => ({
                    title: e.title,
                    id: e.id,
                    data: e,
                })),
            forceReplace: true,
            removedEntryTitle: category.title,
            onClose: async (confirmed, data) => {
                if (!confirmed)
                    return;
                return await removeCategory(category, data);
            }
        }));
    }, [dispatch, removeCategory])

    /**
     * Toggle active state of a category
     * @type {(function(*, *): Promise<boolean|undefined>)|*}
     */
    const toggleActiveCategory = useCallback(async (categoryId, active) => {
        let response;
        if (active) {
            response = await Api.selectPartCategory({
                categoryId: categoryId,
                industryId: fetchedIndustry.id,
            })
        } else {
            response = await Api.unselectPartCategory({
                categoryId: categoryId,
                industryId: fetchedIndustry.id,
            })
        }
        if (!isMounted())
            return false;

        if (response?.resultFlag) {
            const affectedCategoryIds = response.data?.map(e => e.id) ?? [categoryId];
            setCategories(prevState => prevState.map(e => affectedCategoryIds.includes(e.id) ? {...e, active: active} : e));
            invalidateCategoriesCache(fetchedIndustry?.id);
        }
    }, [fetchedIndustry?.id, isMounted, invalidateCategoriesCache])

    /**
     * Extract categories from a category
     * @type {function(*): *[]}
     */
    const extractCategories = useCallback((category) => {
        return [category, ...(category.children?.flatMap(extractCategories) ?? [])]
    }, [])

    /**
     * Open edit category dialog
     * @type {(function(number=, *=): void)}
     */
    const openUpsertCategoryDialog = useCallback((parentId = undefined, category = undefined) => {
        const industryId = fetchedIndustry?.id;
        setUpsertCategoryDialogState({
            open: true,
            industryId: industryId,
            parentId: parentId,
            isRoot: !parentId,
            category: category,
            onSuccess: (editedCategory) => {
                invalidateCategoriesCache(industryId);
                const entity = {
                    ...category,
                    ...editedCategory,
                    parentId: parentId,
                };
                setCategories(prevState => {
                    const found = prevState.find(e => e.id === entity.id);
                    if (found) {
                        return prevState.map(e => e.id === entity.id ? entity : e);
                    }
                    return [...prevState, entity];
                });
            },
        });
    }, [fetchedIndustry?.id, invalidateCategoriesCache])

    const nestedCategories = useMemo(() => {
        const categoryMap = new Map();
        const res = [...(categories ?? [])];
        const nestedCategories = [];
        // Initialize each category with an empty children array and put them in the map
        for (const category of res) {
            categoryMap.set(category.id, {...category, children: []});
        }
        for (const category of res) {
            const cat = categoryMap.get(category.id);
            if (!category.parentId) {
                // If the category has no parentId, it is a root category
                nestedCategories.push(cat);
            } else {
                // Otherwise, find its parent and add it to the parent's children array
                const parent = categoryMap.get(cat.parentId);
                if (parent) {
                    parent.children.push(cat);
                }
            }
        }
        return nestedCategories;
    }, [categories]);

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBlockEnd: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Category Management
                </Typography>
            </Box>
            {
                industries.length > 1 &&
                <Card variant={'elevation'} sx={{padding: 2, marginBlock: 2}}>
                    <TextField
                        select
                        size={'medium'}
                        label={'Industry'}
                        helperText={"Please select an industry to view its categories"}
                        value={fetchedIndustry ?? ""}
                        onChange={e => fetchIndustry({industryId: e.target.value?.id})}
                        fullWidth
                        SelectProps={{renderValue: (value) => value?.title ?? ''}}
                    >
                        {
                            industries.map(industry => (
                                <MenuItem key={industry.id} value={industry}>
                                    {industry.title}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                </Card>
            }
            {
                loadingIndustries &&
                <>
                    <div className='my-2 w-100'/>
                    <LoadingLottieAnimation className={''} width={'80%'}/>
                    <div className='my-2 w-100'/>
                </>
            }
            {
                hasNoIndustries && !loadingIndustries &&
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                    <Typography variant={'body2'} textAlign={'center'} marginBlock={12}>
                        No industries found. Please add an industry or contact your administrator.
                    </Typography>
                </Box>
            }
            {
                !hasNoIndustries &&
                !loadingIndustries &&
                !!fetchedIndustry &&
                <Box sx={{display: 'flex', gap: 1, paddingBlockEnd: 1, position: 'relative', flexWrap: 'wrap'}}>
                    <Card variant={'elevation'} sx={{width: '100%'}}>
                        {
                            loadingCategories &&
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 1}}>
                                {
                                    Array.from({length: 5}).map((_, i) => (
                                        <Skeleton
                                            key={`loading-${i}`}
                                            width={'100%'}
                                            height={90}
                                            sx={{transform: 'none'}}
                                        />
                                    ))
                                }
                            </Box>
                        }
                        {
                            !loadingCategories && categories.length === 0 &&
                            <Box marginBlock={10} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                                <Typography variant={'body2'} textAlign={'center'} marginBlockEnd={2}>
                                    You have no categories in the selected industry. please add a new category.
                                </Typography>
                            </Box>
                        }
                        {
                            !loadingCategories &&
                            <CategoriesTreeView
                                categories={nestedCategories}
                                categoriesFlat={categories}
                                upsert={openUpsertCategoryDialog}
                                removingIds={removingIds}
                                remove={getRemoveConfirmation}
                                toggleActive={toggleActiveCategory}
                            />
                        }

                    </Card>
                </Box>
            }
            <UpsertProductCategoryDialog
                open={upsertCategoryDialogState.open}
                setOpen={() => setUpsertCategoryDialogState(initialUpsertCategoryDialogState)}
                onSuccess={upsertCategoryDialogState.onSuccess}
                category={upsertCategoryDialogState.category}
                industryId={upsertCategoryDialogState.industryId}
                parentId={upsertCategoryDialogState.parentId}
                root={upsertCategoryDialogState.isRoot}
            />
        </>
    );
}

export default CategoryManagementView;
